import { db } from './firebase';
import { collection, addDoc, updateDoc, doc, query, where, orderBy, getDocs, limit, Timestamp } from 'firebase/firestore';
import {
    Notification,
    NotificationType,
    NotificationCategory,
    NotificationPriority,
    NotificationPreferences,
    DeliveryStatus,
    NotificationEntityType
} from '../types';

// ========================================
// NOTIFICATION SERVICE
// ========================================

/**
 * Utility to map notification type to category
 */
export function getNotificationCategory(type: NotificationType): NotificationCategory {
    const categoryMap: Record<NotificationType, NotificationCategory> = {
        // MARKETPLACE
        LISTING_APPROVED: NotificationCategory.MARKETPLACE,
        LISTING_REJECTED: NotificationCategory.MARKETPLACE,
        LISTING_EXPIRED: NotificationCategory.MARKETPLACE,
        LISTING_SOLD: NotificationCategory.MARKETPLACE,
        LISTING_FAVORITED: NotificationCategory.MARKETPLACE,
        PRICE_DROP_ALERT: NotificationCategory.MARKETPLACE,
        BACK_IN_STOCK: NotificationCategory.MARKETPLACE,
        SIMILAR_LISTING_ALERT: NotificationCategory.MARKETPLACE,

        // ORDERS
        ORDER_PLACED: NotificationCategory.ORDERS,
        ORDER_CONFIRMED: NotificationCategory.ORDERS,
        ORDER_SHIPPED: NotificationCategory.ORDERS,
        ORDER_DELIVERED: NotificationCategory.ORDERS,
        ORDER_CANCELLED: NotificationCategory.ORDERS,
        ORDER_DISPUTE_OPENED: NotificationCategory.ORDERS,
        ORDER_DISPUTE_RESOLVED: NotificationCategory.ORDERS,
        ORDER_REVIEW_REQUEST: NotificationCategory.ORDERS,
        ORDER_REMINDER_SHIP: NotificationCategory.ORDERS,
        ORDER_REMINDER_CONFIRM: NotificationCategory.ORDERS,

        // FINANCIAL
        PAYMENT_RECEIVED: NotificationCategory.FINANCIAL,
        PAYMENT_SENT: NotificationCategory.FINANCIAL,
        PAYMENT_PENDING: NotificationCategory.FINANCIAL,
        PAYMENT_RELEASED: NotificationCategory.FINANCIAL,
        REFUND_PROCESSED: NotificationCategory.FINANCIAL,
        WITHDRAWAL_APPROVED: NotificationCategory.FINANCIAL,
        WITHDRAWAL_REJECTED: NotificationCategory.FINANCIAL,
        WITHDRAWAL_COMPLETED: NotificationCategory.FINANCIAL,
        DEPOSIT_SUCCESS: NotificationCategory.FINANCIAL,
        LOW_BALANCE_WARNING: NotificationCategory.FINANCIAL,
        COMMISSION_DEDUCTED: NotificationCategory.FINANCIAL,

        // SOCIAL
        NEW_FOLLOWER: NotificationCategory.SOCIAL,
        NEW_MESSAGE: NotificationCategory.SOCIAL,
        MESSAGE_READ: NotificationCategory.SOCIAL,
        REVIEW_RECEIVED: NotificationCategory.SOCIAL,
        REVIEW_REPLY: NotificationCategory.SOCIAL,
        OFFER_RECEIVED: NotificationCategory.SOCIAL,
        OFFER_ACCEPTED: NotificationCategory.SOCIAL,
        OFFER_REJECTED: NotificationCategory.SOCIAL,
        OFFER_COUNTERED: NotificationCategory.SOCIAL,
        MENTION_IN_COMMENT: NotificationCategory.SOCIAL,

        // SECURITY
        LOGIN_NEW_DEVICE: NotificationCategory.SECURITY,
        LOGIN_NEW_LOCATION: NotificationCategory.SECURITY,
        PASSWORD_CHANGED: NotificationCategory.SECURITY,
        EMAIL_CHANGED: NotificationCategory.SECURITY,
        TWO_FACTOR_ENABLED: NotificationCategory.SECURITY,
        TWO_FACTOR_DISABLED: NotificationCategory.SECURITY,
        SUSPICIOUS_ACTIVITY: NotificationCategory.SECURITY,
        ACCOUNT_LOCKED: NotificationCategory.SECURITY,
        FAILED_LOGIN_ATTEMPTS: NotificationCategory.SECURITY,

        // ADMIN
        RESTRICTION_APPLIED: NotificationCategory.ADMIN,
        RESTRICTION_REMOVED: NotificationCategory.ADMIN,
        APPEAL_STATUS_UPDATED: NotificationCategory.ADMIN,
        ACCOUNT_WARNING: NotificationCategory.ADMIN,
        CONTENT_REMOVED: NotificationCategory.ADMIN,
        VERIFICATION_APPROVED: NotificationCategory.ADMIN,
        VERIFICATION_REJECTED: NotificationCategory.ADMIN,
        BOOST_APPROVED: NotificationCategory.ADMIN,
        BOOST_REJECTED: NotificationCategory.ADMIN,

        // SYSTEM
        MAINTENANCE_SCHEDULED: NotificationCategory.SYSTEM,
        MAINTENANCE_STARTED: NotificationCategory.SYSTEM,
        MAINTENANCE_COMPLETED: NotificationCategory.SYSTEM,
        FEATURE_ANNOUNCEMENT: NotificationCategory.SYSTEM,
        TERMS_UPDATED: NotificationCategory.SYSTEM,
        POLICY_UPDATED: NotificationCategory.SYSTEM,
        APP_UPDATE_AVAILABLE: NotificationCategory.SYSTEM,
        SERVICE_DISRUPTION: NotificationCategory.SYSTEM,

        // MARKETING
        PROMOTIONAL_OFFER: NotificationCategory.MARKETING,
        SEASONAL_SALE: NotificationCategory.MARKETING,
        REFERRAL_BONUS: NotificationCategory.MARKETING,
        LOYALTY_REWARD: NotificationCategory.MARKETING,
        PERSONALIZED_RECOMMENDATION: NotificationCategory.MARKETING,
        NEWSLETTER: NotificationCategory.MARKETING,

        // Legacy
        SALE: NotificationCategory.MARKETPLACE,
        MESSAGE: NotificationCategory.SOCIAL,
        FAVORITE: NotificationCategory.SOCIAL,
        SYSTEM: NotificationCategory.SYSTEM,

        // Order Flow Additional
        ORDER_AWAITING_APPROVAL: NotificationCategory.ORDERS,
        ORDER_APPROVED: NotificationCategory.ORDERS,
        ORDER_REJECTED: NotificationCategory.ORDERS,
        ORDER_AUTO_CANCELLED: NotificationCategory.ORDERS,
        QUESTION_RECEIVED: NotificationCategory.SOCIAL,
        REPLY_RECEIVED: NotificationCategory.SOCIAL,
    };

    return categoryMap[type] || NotificationCategory.SYSTEM;
}

/**
 * Utility to get default priority for notification type
 */
export function getDefaultPriority(type: NotificationType): NotificationPriority {
    const priorityMap: Partial<Record<NotificationType, NotificationPriority>> = {
        // CRITICAL priority
        PAYMENT_RECEIVED: 'CRITICAL',
        PAYMENT_RELEASED: 'CRITICAL',
        WITHDRAWAL_COMPLETED: 'CRITICAL',
        ACCOUNT_LOCKED: 'CRITICAL',
        SUSPICIOUS_ACTIVITY: 'CRITICAL',
        TWO_FACTOR_DISABLED: 'CRITICAL',
        ORDER_DISPUTE_OPENED: 'CRITICAL',

        // HIGH priority
        ORDER_PLACED: 'HIGH',
        ORDER_SHIPPED: 'HIGH',
        ORDER_DELIVERED: 'HIGH',
        WITHDRAWAL_APPROVED: 'HIGH',
        WITHDRAWAL_REJECTED: 'HIGH',
        RESTRICTION_APPLIED: 'HIGH',
        PASSWORD_CHANGED: 'HIGH',
        LOGIN_NEW_DEVICE: 'HIGH',

        // LOW priority
        PROMOTIONAL_OFFER: 'LOW',
        SEASONAL_SALE: 'LOW',
        NEWSLETTER: 'LOW',
        FEATURE_ANNOUNCEMENT: 'LOW',
    };

    return priorityMap[type] || 'MEDIUM';
}

/**
 * Get default notification preferences for a new user
 */
export function getDefaultNotificationPreferences(userId: string): NotificationPreferences {
    return {
        userId,
        categories: {
            MARKETPLACE: { inApp: true, email: true, push: true, sms: false },
            ORDERS: { inApp: true, email: true, push: true, sms: false },
            FINANCIAL: { inApp: true, email: true, push: true, sms: true },
            SOCIAL: { inApp: true, email: false, push: true, sms: false },
            SECURITY: { inApp: true, email: true, push: true, sms: true },
            ADMIN: { inApp: true, email: true, push: false, sms: false },
            SYSTEM: { inApp: true, email: false, push: false, sms: false },
            MARKETING: { inApp: false, email: false, push: false, sms: false },
        },
        doNotDisturb: {
            enabled: false,
            schedule: {
                start: '22:00',
                end: '08:00',
            },
        },
        groupSimilar: true,
        digestEnabled: false,
        digestFrequency: 'daily',
        updatedAt: Date.now(),
    };
}

/**
 * Create a notification
 */
interface CreateNotificationParams {
    userId: string;
    type: NotificationType;
    title: string;
    message: string;
    priority?: NotificationPriority;
    actorId?: string;
    actorName?: string;
    actorAvatar?: string;
    relatedEntityId?: string;
    relatedEntityType?: NotificationEntityType;
    richContent?: Notification['richContent'];
    metadata?: Record<string, any>;
    groupKey?: string;
    scheduledFor?: number;
    expiresAt?: number;
    targetUrl?: string;
    isPromotional?: boolean;
    isUninterruptible?: boolean;
    // Legacy fields for backward compatibility
    listingId?: string;
    data?: any;
}

export async function createNotification(params: CreateNotificationParams): Promise<string> {
    const {
        userId,
        type,
        title,
        message,
        priority,
        actorId,
        actorName,
        actorAvatar,
        relatedEntityId,
        relatedEntityType,
        richContent,
        metadata,
        groupKey,
        scheduledFor,
        expiresAt,
        targetUrl,
        isPromotional,
        isUninterruptible,
        listingId,
        data,
    } = params;

    const category = getNotificationCategory(type);
    const computedPriority = priority || getDefaultPriority(type);

    // Get user preferences (if available)
    // For now, we'll use default channel preferences
    const defaultPrefs = getDefaultNotificationPreferences(userId);
    const channelPrefs = defaultPrefs.categories[category];

    const notification: Omit<Notification, 'id'> = {
        userId,
        category,
        type,
        priority: computedPriority,
        title,
        message,
        richContent,
        actorId,
        actorName,
        actorAvatar,
        relatedEntityId,
        relatedEntityType,
        isRead: false,
        isArchived: false,
        isActionable: false,
        channels: channelPrefs,
        deliveryStatus: {
            inApp: 'delivered', // In-app is always delivered immediately
            email: channelPrefs.email ? 'pending' : undefined,
            push: channelPrefs.push ? 'pending' : undefined,
            sms: channelPrefs.sms ? 'pending' : undefined,
        },
        createdAt: Date.now(),
        scheduledFor,
        expiresAt,
        targetUrl,
        isPromotional,
        isUninterruptible,
        metadata,
        groupKey,
        // Legacy fields
        listingId,
        data,
    };

    const docRef = await addDoc(collection(db, 'notifications'), notification);

    // TODO: Trigger email/push/sms based on channels
    // This will be handled by Cloud Functions

    return docRef.id;
}

/**
 * Mark notification as read
 */
export async function markAsRead(notificationId: string): Promise<void> {
    const notifRef = doc(db, 'notifications', notificationId);
    await updateDoc(notifRef, {
        isRead: true,
        readAt: Date.now(),
    });
}

/**
 * Mark all notifications as read for a user
 */
export async function markAllAsRead(userId: string): Promise<void> {
    const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('isRead', '==', false)
    );

    const snapshot = await getDocs(q);
    const updates = snapshot.docs.map(doc =>
        updateDoc(doc.ref, {
            isRead: true,
            readAt: Date.now(),
        })
    );

    await Promise.all(updates);
}

/**
 * Archive notification
 */
export async function archiveNotification(notificationId: string): Promise<void> {
    const notifRef = doc(db, 'notifications', notificationId);
    await updateDoc(notifRef, {
        isArchived: true,
        archivedAt: Date.now(),
    });
}

/**
 * Get notifications with filtering
 */
interface GetNotificationsParams {
    userId: string;
    category?: NotificationCategory;
    isRead?: boolean;
    limit?: number;
}

export async function getNotifications(params: GetNotificationsParams): Promise<Notification[]> {
    const { userId, category, isRead, limit: queryLimit = 50 } = params;

    let q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('isArchived', '==', false),
        orderBy('createdAt', 'desc'),
        limit(queryLimit)
    );

    // Note: Firestore doesn't support multiple where clauses on different fields without composite index
    // We'll filter in memory for now

    const snapshot = await getDocs(q);
    let notifications = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
    })) as Notification[];

    // Client-side filtering
    if (category) {
        notifications = notifications.filter(n => n.category === category);
    }

    if (isRead !== undefined) {
        notifications = notifications.filter(n => n.isRead === isRead);
    }

    return notifications;
}

/**
 * Group notifications by groupKey
 */
export function groupNotifications(notifications: Notification[]): Map<string, Notification[]> {
    const grouped = new Map<string, Notification[]>();

    for (const notif of notifications) {
        if (notif.groupKey) {
            const existing = grouped.get(notif.groupKey) || [];
            existing.push(notif);
            grouped.set(notif.groupKey, existing);
        }
    }

    return grouped;
}

/**
 * Helper to migrate legacy notification to new format
 */
export function migrateLegacyNotification(legacy: any): Partial<Notification> {
    return {
        category: 'SYSTEM',
        priority: 'MEDIUM',
        title: 'Bildirim',
        message: legacy.data?.message || 'Yeni bildirim',
        channels: { inApp: true, email: false, push: false, sms: false },
        deliveryStatus: { inApp: 'delivered' },
        isArchived: false,
        isActionable: false,
        ...legacy,
    };
}
